import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'
import { ROUTES } from '@/constants'
import { useAuthStore } from '@/store/authStore'

// Layouts
import MainLayout from '@/layouts/MainLayout'
import AuthLayout from '@/layouts/AuthLayout'

// Auth Pages
import LoginPage from '@/features/auth/pages/LoginPage'
import SplashPage from '@/features/auth/pages/SplashPage'

// Public Pages
import CustomerFeedbackPage from '@/features/tasks/pages/CustomerFeedbackPage'

// Main Pages
import DashboardPage from '@/features/dashboard/pages/DashboardPage'

// Inquiry Pages
import InquiryListPage from '@/features/inquiries/pages/InquiryListPage'
import NewInquiryPage from '@/features/inquiries/pages/NewInquiryPage'
import InquiryDetailPage from '@/features/inquiries/pages/InquiryDetailPage'

// Installation Pages
import InstallationListPage from '@/features/installations/pages/InstallationListPage'
import InstallationDetailPage from '@/features/installations/pages/InstallationDetailPage'
import InstallationWorkflowPage from '@/features/installations/pages/InstallationWorkflowPage'

// Ticket Pages
import TicketListPage from '@/features/tickets/pages/TicketListPage'
import TicketDetailPage from '@/features/tickets/pages/TicketDetailPage'

// Follow-up Pages
import FollowUpListPage from '@/features/followups/pages/FollowUpListPage'

// Task Pages
import TaskListPage from '@/features/tasks/pages/TaskListPage'
import NewTaskPage from '@/features/tasks/pages/NewTaskPage'
import EngineerTaskPage from '@/features/tasks/pages/EngineerTaskPage'
import TaskDetailPage from '@/features/tasks/pages/TaskDetailPage'

// Engineer Pages
import EngineerListPage from '@/features/engineers/pages/EngineerListPage'

// BSNL Customer Pages
import BsnlCustomerListPage from '@/features/customers/pages/BsnlCustomerListPage'

// Network Topology
import NetworkTopologyPage from '@/features/network/pages/NetworkTopologyPage'

// Report Pages
import ReportsPage from '@/features/reports/pages/ReportsPage'

// Settings Pages
import SettingsPage from '@/features/settings/pages/SettingsPage'
import ProfilePage from '@/features/settings/pages/ProfilePage'

// Other
import NotificationsPage from '@/features/notifications/pages/NotificationsPage'
import OfflineSyncPage from '@/features/offline-sync/pages/OfflineSyncPage'
import NotFoundPage from '@/features/auth/pages/NotFoundPage'

// Route Guards
import ProtectedRoute from './ProtectedRoute'
import RoleGuard from './RoleGuard'

// Role-aware task page selector
const TasksRoute = () => {
  const { user } = useAuthStore()
  return user?.role === 'engineer' ? <EngineerTaskPage /> : <TaskListPage />
}

const routes: RouteObject[] = [
  // Auth Routes
  {
    element: <AuthLayout />,
    children: [
      { path: ROUTES.SPLASH, element: <SplashPage /> },
      { path: ROUTES.LOGIN, element: <LoginPage /> }
    ]
  },

  // Protected Routes
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <MainLayout />,
        children: [
          // Dashboard
          { path: ROUTES.DASHBOARD, element: <DashboardPage /> },

          // Inquiries
          { path: ROUTES.INQUIRIES, element: <InquiryListPage /> },
          { path: ROUTES.NEW_INQUIRY, element: <NewInquiryPage /> },
          { path: ROUTES.INQUIRY_DETAIL, element: <InquiryDetailPage /> },

          // Installations
          { path: ROUTES.INSTALLATIONS, element: <InstallationListPage /> },
          { path: ROUTES.INSTALLATION_DETAIL, element: <InstallationDetailPage /> },
          { path: ROUTES.INSTALLATION_WORKFLOW, element: <InstallationWorkflowPage /> },

          // Tickets
          { path: ROUTES.TICKETS, element: <TicketListPage /> },
          { path: ROUTES.TICKET_DETAIL, element: <TicketDetailPage /> },

          // Follow-ups
          { path: ROUTES.FOLLOWUPS, element: <FollowUpListPage /> },

          // Tasks (admin → TaskListPage, engineer → EngineerTaskPage)
          { path: ROUTES.TASKS, element: <TasksRoute /> },
          { path: ROUTES.NEW_TASK, element: <RoleGuard allowedRoles={['admin']}><NewTaskPage /></RoleGuard> },
          { path: ROUTES.TASK_DETAIL, element: <TaskDetailPage /> },

          // Engineers (all authenticated users — admin gets full CRUD, engineer gets view-only)
          { path: ROUTES.ENGINEERS, element: <EngineerListPage /> },

          // Customers (Admin/Supervisor only)
          {
            path: ROUTES.CUSTOMERS,
            element: (
              <RoleGuard allowedRoles={['admin', 'supervisor']}>
                <BsnlCustomerListPage />
              </RoleGuard>
            )
          },

          // Network Topology (Admin/Supervisor only)
          {
            path: ROUTES.NETWORK_TOPOLOGY,
            element: (
              <RoleGuard allowedRoles={['admin', 'supervisor']}>
                <NetworkTopologyPage />
              </RoleGuard>
            )
          },

          // Reports (Admin/Supervisor only)
          {
            path: ROUTES.REPORTS,
            element: (
              <RoleGuard allowedRoles={['admin', 'supervisor']}>
                <ReportsPage />
              </RoleGuard>
            )
          },

          // Settings & Profile
          { path: ROUTES.SETTINGS, element: <SettingsPage /> },
          { path: ROUTES.PROFILE, element: <ProfilePage /> },

          // Notifications
          { path: ROUTES.NOTIFICATIONS, element: <NotificationsPage /> },

          // Offline Sync
          { path: ROUTES.OFFLINE_SYNC, element: <OfflineSyncPage /> },

          // Default redirect
          { path: '/', element: <Navigate to={ROUTES.DASHBOARD} replace /> }
        ]
      }
    ]
  },

  // Public Feedback Route (no auth required)
  { path: '/feedback/:taskId', element: <CustomerFeedbackPage /> },

  // Catch all
  { path: '*', element: <NotFoundPage /> }
]

export const router = createBrowserRouter(routes)
export default router
